import React, { useState } from 'react';

// Define types for our component
interface XmlToXsdProps {}

// XML to XSD conversion function
const xmlToXsd = (xml: string): string => {
  try {
    // Create a DOM parser and parse the XML string
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "application/xml");
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XML Parsing Error: " + parseError[0].textContent);
    }
    
    // Start building XSD content
    let xsdContent = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xsdContent += '<xs:schema xmlns:xs="http://www.w3.org/2001/XMLSchema">\n';
    
    // Process the root element
    const rootElement = xmlDoc.documentElement;
    xsdContent += processElement(rootElement, 1);
    
    // Close the schema tag
    xsdContent += '</xs:schema>';
    
    return xsdContent;
  } catch (error) {
    throw error;
  }
};

// Helper function to process an element and generate XSD
const processElement = (element: Element, indentLevel: number): string => {
  const indent = '  '.repeat(indentLevel);
  const elementName = element.nodeName;
  let result = '';
  
  // Start element declaration
  result += `${indent}<xs:element name="${elementName}">\n`;
  result += `${indent}  <xs:complexType>\n`;
  
  // Check if we have attributes to process
  const attributes = element.attributes;
  const hasAttributes = attributes && attributes.length > 0;
  
  // Process child elements
  const childElements = Array.from(element.children);
  
  // If we have child elements
  if (childElements.length > 0) {
    result += `${indent}    <xs:sequence>\n`;
    
    // Track elements we've already processed to handle repeated elements
    const processedElements = new Map<string, { count: number, element: Element }>();
    
    // First pass: count occurrences of each element name
    childElements.forEach(child => {
      const childName = child.nodeName;
      if (processedElements.has(childName)) {
        const entry = processedElements.get(childName)!;
        entry.count += 1;
      } else {
        processedElements.set(childName, { count: 1, element: child });
      }
    });
    
    // Second pass: process each unique child element
    processedElements.forEach((entry, childName) => {
      const { count, element } = entry;
      
      // If element appears multiple times, make it unbounded
      const minOccurs = 0;
      const maxOccurs = count > 1 ? "unbounded" : "1";
      
      // Check if this element has its own children
      if (element.children.length > 0 || element.attributes.length > 0) {
        // Complex child element
        result += processElement(element, indentLevel + 2);
        
        // Update the last element tag to include minOccurs/maxOccurs if needed
        if (count > 1) {
          // Remove the last closing tag
          result = result.substring(0, result.lastIndexOf(`<`));
          // Add minOccurs/maxOccurs attributes
          result += `${indent}    <xs:element name="${childName}" minOccurs="${minOccurs}" maxOccurs="${maxOccurs}">\n`;
        }
      } else {
        // Simple child element with just text content
        const textContent = element.textContent || "";
        
        // Determine the data type based on content
        let dataType = guessDataType(textContent);
        
        result += `${indent}    <xs:element name="${childName}" type="xs:${dataType}" minOccurs="${minOccurs}" maxOccurs="${maxOccurs}"/>\n`;
      }
    });
    
    result += `${indent}    </xs:sequence>\n`;
  } else if (element.textContent && element.textContent.trim() !== "") {
    // Element with just text content
    result += `${indent}    <xs:simpleContent>\n`;
    result += `${indent}      <xs:extension base="xs:${guessDataType(element.textContent)}">\n`;
    
    // Process attributes if any
    if (hasAttributes) {
      for (let i = 0; i < attributes.length; i++) {
        const attr = attributes[i];
        result += `${indent}        <xs:attribute name="${attr.name}" type="xs:${guessDataType(attr.value)}"/>\n`;
      }
    }
    
    result += `${indent}      </xs:extension>\n`;
    result += `${indent}    </xs:simpleContent>\n`;
  } else {
    // Empty element or element with only attributes
    if (hasAttributes) {
      for (let i = 0; i < attributes.length; i++) {
        const attr = attributes[i];
        result += `${indent}    <xs:attribute name="${attr.name}" type="xs:${guessDataType(attr.value)}"/>\n`;
      }
    }
  }
  
  // Close complexType and element tags
  result += `${indent}  </xs:complexType>\n`;
  result += `${indent}</xs:element>\n`;
  
  return result;
};

// Helper function to guess the XML Schema data type based on a value
const guessDataType = (value: string): string => {
  if (!value || value.trim() === "") {
    return "string";
  }
  
  // Check if it's a boolean
  if (value.toLowerCase() === "true" || value.toLowerCase() === "false") {
    return "boolean";
  }
  
  // Check if it's an integer
  if (/^-?\d+$/.test(value)) {
    return "integer";
  }
  
  // Check if it's a decimal/float
  if (/^-?\d+\.\d+$/.test(value)) {
    return "decimal";
  }
  
  // Check if it's a date
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return "date";
  }
  
  // Check if it's a dateTime
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
    return "dateTime";
  }
  
  // Default to string
  return "string";
};

// Function to format/prettify XSD
const formatXsd = (xsd: string): string => {
  try {
    const parser = new DOMParser();
    const xsdDoc = parser.parseFromString(xsd, "application/xml");
    
    // Check for parsing errors
    const parseError = xsdDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XSD Parsing Error: " + parseError[0].textContent);
    }
    
    // Function to pretty print XML with indentation
    const prettyPrintXml = (node: Node, indent: string = ''): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue?.trim();
        return text ? indent + text + '\n' : '';
      }
      
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        let result = indent + '<' + element.nodeName;
        
        // Add attributes
        for (let i = 0; i < element.attributes.length; i++) {
          const attr = element.attributes[i];
          result += ` ${attr.nodeName}="${attr.nodeValue}"`;
        }
        
        // Check if there are any child nodes
        if (element.childNodes.length === 0) {
          result += '/>\n';
          return result;
        }
        
        result += '>\n';
        
        // Process child nodes
        for (let i = 0; i < element.childNodes.length; i++) {
          result += prettyPrintXml(element.childNodes[i], indent + '  ');
        }
        
        result += indent + '</' + element.nodeName + '>\n';
        return result;
      }
      
      return '';
    };
    
    // Add XML declaration
    let formattedXsd = '<?xml version="1.0" encoding="UTF-8"?>\n';
    
    // Format the document element
    formattedXsd += prettyPrintXml(xsdDoc.documentElement);
    
    return formattedXsd;
  } catch (e) {
    // If there's an error in formatting, return the original XSD
    console.error("Error formatting XSD:", e);
    return xsd;
  }
};

// Main component to convert XML to XSD
const XmlToXsd: React.FC<XmlToXsdProps> = () => {
  // State for the XML input
  const [xmlInput, setXmlInput] = useState<string>('');
  // State for the XSD output
  const [xsdOutput, setXsdOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('schema');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isXsdGenerated, setIsXsdGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for includeAnnotations option
  const [includeAnnotations, setIncludeAnnotations] = useState<boolean>(true);
  // State for XSD target namespace
  const [targetNamespace, setTargetNamespace] = useState<string>('');
  // State for default element form qualified
  const [elementFormDefault, setElementFormDefault] = useState<string>('unqualified');

  // Handle XML input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setXmlInput(e.target.value);
    setError('');
    setIsXsdGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    const exampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<library>
  <book id="bk101" category="fiction">
    <title>The Great Gatsby</title>
    <author>F. Scott Fitzgerald</author>
    <year>1925</year>
    <price>9.99</price>
    <tags>
      <tag>classic</tag>
      <tag>american literature</tag>
      <tag>jazz age</tag>
    </tags>
    <available>true</available>
  </book>
  <book id="bk102" category="non-fiction">
    <title>A Brief History of Time</title>
    <author>Stephen Hawking</author>
    <year>1988</year>
    <price>12.50</price>
    <tags>
      <tag>science</tag>
      <tag>physics</tag>
      <tag>cosmology</tag>
    </tags>
    <available>false</available>
  </book>
</library>`;

    setXmlInput(exampleXml);
    setError('');
    setIsXsdGenerated(false);
    setXsdOutput('');
  };

  // Format/Beautify XML
  const formatXml = () => {
    try {
      if (!xmlInput.trim()) {
        setError('Please enter XML data to format');
        return;
      }
      
      // Parse the XML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlInput, "application/xml");
      
      // Check for parsing errors
      const parseError = xmlDoc.getElementsByTagName("parsererror");
      if (parseError.length > 0) {
        throw new Error("XML Parsing Error: " + parseError[0].textContent);
      }
      
      // Serialize back to string with pretty formatting
      const serializer = new XMLSerializer();
      let formattedXml = '';
      
      // Function to pretty print XML with indentation
      const prettyPrintXml = (node: Node, indent: string = ''): string => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.nodeValue?.trim();
          return text ? indent + text + '\n' : '';
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          let result = indent + '<' + element.nodeName;
          
          // Add attributes
          for (let i = 0; i < element.attributes.length; i++) {
            const attr = element.attributes[i];
            result += ` ${attr.nodeName}="${attr.nodeValue}"`;
          }
          
          // Check if there are any child nodes
          if (element.childNodes.length === 0) {
            result += '/>\n';
            return result;
          }
          
          result += '>\n';
          
          // Process child nodes
          for (let i = 0; i < element.childNodes.length; i++) {
            result += prettyPrintXml(element.childNodes[i], indent + '  ');
          }
          
          result += indent + '</' + element.nodeName + '>\n';
          return result;
        }
        
        return '';
      };
      
      // Add XML declaration
      formattedXml += '<?xml version="1.0" encoding="UTF-8"?>\n';
      
      // Format the document element
      formattedXml += prettyPrintXml(xmlDoc.documentElement);
      
      setXmlInput(formattedXml);
      setError('');
    } catch (e) {
      setError(`Error formatting XML: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Convert XML to XSD
  const convertXmlToXsd = () => {
    setError('');
    setIsLoading(true);
    setIsXsdGenerated(false);
    setXsdOutput('');
    
    if (!xmlInput.trim()) {
      setError('Please enter XML data');
      setIsLoading(false);
      return;
    }

    try {
      // Generate the XSD
      let xsdContent = xmlToXsd(xmlInput);
      
      // Apply custom settings if in advanced mode
      if (showAdvanced) {
        // Add target namespace if provided
        if (targetNamespace) {
          xsdContent = xsdContent.replace(
            '<xs:schema xmlns:xs="http://www.w3.org/2001/XMLSchema">',
            `<xs:schema xmlns:xs="http://www.w3.org/2001/XMLSchema" targetNamespace="${targetNamespace}" elementFormDefault="${elementFormDefault}">`
          );
        } else {
          // Just add elementFormDefault if no target namespace
          xsdContent = xsdContent.replace(
            '<xs:schema xmlns:xs="http://www.w3.org/2001/XMLSchema">',
            `<xs:schema xmlns:xs="http://www.w3.org/2001/XMLSchema" elementFormDefault="${elementFormDefault}">`
          );
        }
        
        // Add annotations if enabled
        if (includeAnnotations) {
          xsdContent = xsdContent.replace(
            '<xs:schema',
            `<!-- XSD Schema generated from XML document -->
<xs:schema`
          );
        }
      }
      
      // Format the XSD output
      const formattedXsd = formatXsd(xsdContent);
      
      setXsdOutput(formattedXsd);
      setIsXsdGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting XML to XSD: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setXmlInput('');
    setXsdOutput('');
    setError('');
    setFileName('schema');
    setIncludeAnnotations(true);
    setTargetNamespace('');
    setElementFormDefault('unqualified');
    setIsXsdGenerated(false);
  };

  // Copy XSD to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(xsdOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download XSD file
  const downloadXsd = () => {
    const blob = new Blob([xsdOutput], { type: 'application/xml;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'schema'}.xsd`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">XML to XSD Schema Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Generate XML Schema Definition (XSD) from XML data. Paste your XML below to create an XSD schema file.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="xmlInput" className="font-medium text-gray-700">
                XML Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={formatXml}
                >
                  Format XML
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setXmlInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="xmlInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={xmlInput}
              onChange={handleInputChange}
              placeholder="Paste your XML data here, e.g.,
<?xml version='1.0' encoding='UTF-8'?>
<library>
  <book>
    <title>Sample Book</title>
    <author>John Doe</author>
  </book>
</library>"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                Schema File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeAnnotations"
                    checked={includeAnnotations}
                    onChange={(e) => setIncludeAnnotations(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeAnnotations" className="ml-2 block text-sm text-gray-700">
                    Include Annotations
                  </label>
                </div>
                
                <div>
                  <label htmlFor="targetNamespace" className="block text-sm font-medium text-gray-700 mb-1">
                    Target Namespace (optional)
                  </label>
                  <input
                    type="text"
                    id="targetNamespace"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={targetNamespace}
                    onChange={(e) => setTargetNamespace(e.target.value)}
                    placeholder="e.g., http://www.example.com/schema"
                  />
                </div>
                
                <div>
                  <label htmlFor="elementFormDefault" className="block text-sm font-medium text-gray-700 mb-1">
                    Element Form Default
                  </label>
                  <select
                    id="elementFormDefault"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={elementFormDefault}
                    onChange={(e) => setElementFormDefault(e.target.value)}
                  >
                    <option value="qualified">qualified</option>
                    <option value="unqualified">unqualified</option>
                  </select>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertXmlToXsd}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to XSD'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              XSD Schema Output
            </label>
            
            {isXsdGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy XSD'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadXsd}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isXsdGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    XSD schema output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isXsdGenerated && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="xsdOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={xsdOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isXsdGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">XML Successfully Converted to XSD Schema!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated XSD schema for use in XML validation or documentation.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About XML to XSD Schema Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool analyzes your XML data and generates an XML Schema Definition (XSD) that describes the structure, elements, and data types of your XML document. The generated XSD can be used to validate XML documents against the inferred schema structure.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Structure Analysis</strong> - Intelligently determines the structure of your XML</li>
              <li><strong>Data Type Detection</strong> - Automatically infers appropriate XSD data types</li>
              <li><strong>Complex Type Handling</strong> - Properly manages nested elements and complex structures</li>
              <li><strong>Attribute Support</strong> - Preserves and defines XML attributes in the schema</li>
              <li><strong>Array Detection</strong> - Identifies repeated elements and sets appropriate occurrence constraints</li>
              <li><strong>Namespace Support</strong> - Optional target namespace configuration</li>
              <li><strong>Clean Output</strong> - Generates well-formatted, readable XSD</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Understanding XSD Schemas</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>XSD (XML Schema Definition) is an XML-based language for defining the structure of XML documents</li>
              <li>XSD schemas allow validation of XML documents against a defined structure</li>
              <li>Elements can be defined as simple types (containing just data) or complex types (containing child elements)</li>
              <li>Attributes are defined using the xs:attribute element</li>
              <li>Data types in XSD include string, integer, decimal, boolean, date, and many others</li>
              <li>minOccurs and maxOccurs attributes define how many times an element can appear</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">XSD Conversion Limitations</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>The schema is generated based on a single XML instance, so it may not cover all possible variations</li>
              <li>Data type inference is based on values in the sample XML and may need manual adjustment</li>
              <li>For complex XML documents, the generated schema may need refinement</li>
              <li>Element order within sequence is preserved, but does not detect choice or all compositor patterns</li>
              <li>This tool generates a basic schema and may not implement advanced XSD features</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use a sample XML document that contains all possible elements and attributes</li>
              <li>Include representative data values to enable accurate data type detection</li>
              <li>Format your XML first for better readability and processing</li>
              <li>For more complex needs, consider refining the generated XSD manually</li>
              <li>Use advanced options to configure namespace settings if needed</li>
              <li>Validate the generated XSD with specialized XML schema validation tools</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XmlToXsd;